import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { routes, stripOrganizationScopePrefix } from 'constants/routes';
import { sidebarStrings } from 'constants/strings';
import { cn } from 'utils/cn';
import { useOrgPath } from 'hooks/useOrgPath';

import { Icon } from '../Icon';
import { sidebarClasses } from './styles';

type SideBarSettingsSectionProps = {
  isAdmin?: boolean;
};

export function SideBarSettingsSection({
  isAdmin = false,
}: SideBarSettingsSectionProps) {
  const location = useLocation();
  const orgPathFor = useOrgPath();
  const pathname = stripOrganizationScopePrefix(location.pathname);
  const isSettingsRoute =
    pathname.startsWith('/settings') ||
    pathname.startsWith('/message-settings');
  const [expanded, setExpanded] = useState(isSettingsRoute);

  useEffect(() => {
    if (isSettingsRoute) {
      setExpanded(true);
    }
  }, [isSettingsRoute]);

  return (
    <div className="grid gap-1">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className={cn(
          'group flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition',
          expanded
            ? 'border-border/70 bg-white/70 text-ink'
            : 'border-border/70 bg-white/70 text-ink hover:-translate-y-0.5 hover:border-ink/10 hover:bg-white',
        )}
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-3">
          <Icon icon="menuSettings" className={sidebarClasses.navIcon(false)} />
          <span>{sidebarStrings.settingsGroupLabel}</span>
        </span>
        <Icon
          icon={expanded ? 'chevronUp' : 'chevronDown'}
          className="h-4 w-4 text-muted"
        />
      </button>
      {expanded && (
        <div className="ml-2 grid gap-1 border-l border-border/50 pl-3">
          <NavLink
            to={orgPathFor(routes.MESSAGE_SETTINGS)}
            className={({ isActive }) =>
              cn(
                'flex items-center rounded-xl px-3 py-2 text-sm font-medium transition',
                isActive
                  ? 'bg-ink text-white shadow-sm'
                  : 'text-ink hover:bg-white/80',
              )
            }
          >
            {sidebarStrings.navLabels.messageSettings}
          </NavLink>
          <NavLink
            to={orgPathFor(routes.RICH_MENU)}
            className={({ isActive }) =>
              cn(
                'flex items-center rounded-xl px-3 py-2 text-sm font-medium transition',
                isActive
                  ? 'bg-ink text-white shadow-sm'
                  : 'text-ink hover:bg-white/80',
              )
            }
            end
          >
            {sidebarStrings.navLabels.richMenu}
          </NavLink>
          {isAdmin && (
            <NavLink
              to={routes.AI_KNOWLEDGE}
              className={({ isActive }) =>
                cn(
                  'flex items-center rounded-xl px-3 py-2 text-sm font-medium transition',
                  isActive
                    ? 'bg-ink text-white shadow-sm'
                    : 'text-ink hover:bg-white/80',
                )
              }
            >
              {sidebarStrings.navLabels.aiKnowledge}
            </NavLink>
          )}
          <NavLink
            to={orgPathFor(routes.AUTO_REPLY_MESSAGE)}
            className={({ isActive }) =>
              cn(
                'flex items-center rounded-xl px-3 py-2 text-sm font-medium transition',
                isActive
                  ? 'bg-ink text-white shadow-sm'
                  : 'text-ink hover:bg-white/80',
              )
            }
          >
            {sidebarStrings.navLabels.autoReplyMessage}
          </NavLink>
          <NavLink
            to={orgPathFor(routes.RICH_MESSAGE)}
            className={({ isActive }) =>
              cn(
                'flex items-center rounded-xl px-3 py-2 text-sm font-medium transition',
                isActive
                  ? 'bg-ink text-white shadow-sm'
                  : 'text-ink hover:bg-white/80',
              )
            }
          >
            {sidebarStrings.navLabels.richMessage}
          </NavLink>
          <NavLink
            to={orgPathFor(routes.CARD_TYPE_MESSAGE)}
            className={({ isActive }) =>
              cn(
                'flex items-center rounded-xl px-3 py-2 text-sm font-medium transition',
                isActive
                  ? 'bg-ink text-white shadow-sm'
                  : 'text-ink hover:bg-white/80',
              )
            }
          >
            {sidebarStrings.navLabels.cardTypeMessage}
          </NavLink>
        </div>
      )}
    </div>
  );
}
